import { useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router';
import { MobileDrawer } from '../components/shell/MobileDrawer';
import { MobileTopBar, MobileTopBarSkeleton } from '../components/shell/MobileTopBar';
import { Rail, RailSkeleton } from '../components/shell/Rail';
import { authClient, useSession } from '../lib/auth-client';
import { useDemoRefSubmit } from '../hooks/useDemoRefSubmit';
import { useT } from '../locales/context';

export function Protected() {
  const { data: session, isPending } = useSession();
  const t = useT();
  // Playground-signup-card cycle (THI-33, 2026-08-22 spec §B) — `Protected`, not
  // `LandingResolver`: it wraps every authenticated route, so a visitor who lands on `/bots`
  // or a verification deep link is still attributed. Called above the `isPending` early
  // return per the rules of hooks; `enabled` itself gates on the session actually existing.
  useDemoRefSubmit(Boolean(session) && !isPending);
  // Dashboard-shell task-5: the drawer's open state and the hamburger's DOM node (so closing
  // can return focus to it) live here — `MobileTopBar` renders two triggers for the same
  // drawer (hamburger + avatar, README §1 mobile table) and `MobileDrawer` is this layout's
  // sibling, not a descendant of either, so this is the natural shared owner.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Dashboard-shell task-4: the old bare loading label is replaced with a skeleton rail — the
  // first thing every visitor sees while the session resolves (handoff README §6: never a bare
  // loading label, including this layout's own session check). Task 5 adds the mobile
  // equivalent — below 1024px the rail is `hidden` outright, so without this the mobile
  // session-loading frame was blank.
  if (isPending) {
    return (
      <div className="flex min-h-screen flex-col">
        <MobileTopBarSkeleton />
        <div className="flex min-h-0 flex-1">
          <RailSkeleton />
          <main className="min-w-0 flex-1" />
        </div>
      </div>
    );
  }
  if (!session) return <Navigate to="/sign-in" replace />;

  function handleSignOut() {
    authClient.signOut({
      fetchOptions: { onSuccess: () => location.assign('/sign-in') },
    });
  }

  // Task 3 of the admin-platform cycle (spec §1) — the `adminClient()` plugin (Task 1)
  // augments the session object with `impersonatedBy` when the CURRENT session is one an
  // admin started via `impersonateUser`: at that point `session.user` IS the impersonated
  // owner (that's the whole trick — the cookie now belongs to their session), so the email
  // shown below is exactly whose shoes we're standing in.
  const impersonatedBy = session.session.impersonatedBy;

  function handleStopImpersonating() {
    // Back to /admin, not the owner dashboard — exiting impersonation resumes ADMIN work
    // (Johnny, 2026-08-20). Hard navigation for the same cache-reset reason as entry.
    authClient.admin.stopImpersonating().then(() => location.assign('/admin'));
  }

  return (
    <>
      {/* `inert` while the drawer is open (React 19 renders/removes the boolean attribute
          directly): the scrim already blocks pointer interaction, this is what keeps the top
          bar, rail, and page content out of the Tab order too — the drawer itself lives
          outside this wrapper so it stays interactive. */}
      <div inert={drawerOpen} className="flex min-h-screen flex-col">
        {impersonatedBy && (
          <div
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-[13px] font-medium"
            style={{
              color: 'var(--st-paused)',
              backgroundColor: 'color-mix(in srgb, var(--st-paused) var(--st-wash), transparent)',
            }}
          >
            <span>{t.shell.impersonating({ email: session.user.email })}</span>
            <button
              type="button"
              onClick={handleStopImpersonating}
              className="font-semibold underline underline-offset-2"
            >
              {t.shell.stopImpersonating}
            </button>
          </div>
        )}
        {/* Dashboard-shell task-4: the header (logo + avatar dropdown) is gone — the rail now
            owns the brand lockup and the account control. Below 1024px (task-5) the rail
            hides and `MobileTopBar` + `MobileDrawer` take over. */}
        <MobileTopBar
          ref={menuButtonRef}
          session={session}
          drawerOpen={drawerOpen}
          onOpenMenu={() => setDrawerOpen(true)}
          onSignOut={handleSignOut}
        />
        <div className="flex min-h-0 flex-1">
          <Rail session={session} onSignOut={handleSignOut} />
          <main className="min-w-0 flex-1 overflow-y-auto">
            {/* 1200px container per the design handoff's dashboard frames; padding steps up to
                the handoff's desktop values (26px 32px 44px) at the rail's own breakpoint. */}
            <div className="mx-auto max-w-[1200px] p-4 sm:p-6 lg:px-8 lg:pt-[26px] lg:pb-11">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        session={session}
        onSignOut={handleSignOut}
        triggerRef={menuButtonRef}
      />
    </>
  );
}
